import { computed, shallowRef } from "vue";

/**
 * @typedef {import("./useFinderFilterRegistry").FinderFilterRegistration} FinderFilterRegistration
 * @typedef {import("#ce/shared/finder-contribution-protocol").FinderContribution} FinderContribution
 * @typedef {FinderFilterRegistration | (FinderContribution & {contributionId: string})} FinderMenuItem
 */

/**
 * The menu's view of the bar's registry: which filters it lists, how many the visitor has applied,
 * and the three things it can ask of them.
 *
 * Every value is derived from the two registries, so the menu holds no state of its own and cannot
 * disagree with the bar.
 *
 * @param {import("vue").ShallowRef<Map<HTMLElement, FinderFilterRegistration>>} registry
 * @param {import("vue").ShallowRef<Map<string, FinderContribution>>} [contributions]
 * @param {() => void} [prune] - refresh local DOM membership before acting.
 * @returns {{
 *   menuItems: import("vue").ComputedRef<FinderMenuItem[]>,
 *   appliedCount: import("vue").ComputedRef<number>,
 *   resettableCount: import("vue").ComputedRef<number>,
 *   open: (menuItem: FinderMenuItem) => void,
 *   clear: (menuItem: FinderMenuItem) => void,
 *   clearAll: () => void,
 *   closeLocalFilters: () => void,
 * }}
 */
export function useFinderFilterMenu(registry, contributions = shallowRef(new Map()), prune = () => {}) {
  /**
   * Document order, not the bar's active-first order: the menu is an index of the filters, and a
   * menu item that moves as the visitor applies filters is one they have to hunt for.
   *
   * Contributions precede the local filters in connection order. Withheld entries are omitted.
   */
  const menuItems = computed(() => [...contributionMenuItems(), ...filterMenuItems()]);

  function contributionMenuItems() {
    return [...contributions.value.values()].filter(isListed).map(toContributionMenuItem);
  }

  function filterMenuItems() {
    return [...registry.value.values()].filter(isListed).sort(byDocumentPosition);
  }

  function findCurrentEntry(menuItem) {
    prune();
    const entry = findEntry(menuItem);
    if (entry && isListed(entry)) {
      return entry;
    }
  }

  function findEntry(menuItem) {
    if (menuItem.contributionId !== undefined) {
      return contributions.value.get(menuItem.contributionId);
    }
    return registry.value.get(menuItem.element);
  }

  /** What the trigger counts: every filter the visitor has applied, clearable or not. */
  const appliedCount = computed(() => menuItems.value.filter(isApplied).length);

  /**
   * What Reset All acts on, and whether it appears at all. A filter the visitor may not clear still
   * counts as applied, so the two collections differ wherever a required filter holds a value.
   */
  const resettable = computed(() => menuItems.value.filter(isClearable));

  const resettableCount = computed(() => resettable.value.length);

  /** @param {FinderMenuItem} menuItem */
  function open(menuItem) {
    const entry = findCurrentEntry(menuItem);
    if (!entry) {
      return;
    }
    if (menuItem.contributionId !== undefined) {
      closeLocalFilters();
      entry.actions.reveal?.();
      return;
    }
    entry.actions.open();
  }

  function closeLocalFilters() {
    prune();
    for (const entry of registry.value.values()) {
      if (entry.state.open) {
        entry.actions.close();
      }
    }
  }

  /** @param {FinderMenuItem} menuItem */
  function clear(menuItem) {
    const entry = findCurrentEntry(menuItem);
    if (entry && isClearable(entry)) {
      entry.actions.clear();
    }
  }

  function clearAll() {
    resettable.value.forEach(clear);
  }

  return { menuItems, appliedCount, resettableCount, open, clear, clearAll, closeLocalFilters };
}

function isListed(entry) {
  return !entry.state.withheld;
}

function toContributionMenuItem(entry) {
  return { ...entry, id: `contribution:${entry.id}`, contributionId: entry.id };
}

/**
 * Two filters slotted inside one wrapper share the wrapper's `documentIndex`, so the tie is
 * settled by asking the DOM which of the two comes first.
 *
 * @param {FinderFilterRegistration} a
 * @param {FinderFilterRegistration} b
 */
function byDocumentPosition(a, b) {
  if (a.documentIndex !== b.documentIndex) {
    return a.documentIndex - b.documentIndex;
  }

  return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

function isApplied(entry) {
  return entry.state.active;
}

function isClearable(entry) {
  return isApplied(entry) && entry.state.clearable;
}
